/**
 * IXVerify API client (WP-3.4)
 *
 * Thin wrapper over the shared authenticated axios client (services/api.ts) that
 * talks to the platform IXVerify router at /api/ixverify. It replaces every direct
 * supabase-js DATA call the module used to make (`.from()`, `.rpc()`, `.storage`,
 * and the two `functions.invoke()` OCR/report paths).
 *
 * Design: helpers return the SAME `{ data, error }` shape supabase-js returned, so
 * existing call sites (`const { data, error } = await ...`) change only which
 * function they call, not how they read the result. `error` is `null` on success or
 * `{ message }` on failure (mirroring supabase's PostgrestError.message usage).
 *
 * Auth + site context: api.ts already attaches the Supabase JWT (Authorization) and
 * X-Site-Id/X-User-Id headers on every request. Auth itself still lives on Supabase
 * (Phase 8); this module only moves DATA off the client.
 */

import api from '../../services/api';

type Result<T> = { data: T; error: { message: string } | null };

function toError(err: unknown): { message: string } {
  // Prefer a server-provided error message ({ error } or { message }).
  const anyErr = err as any;
  const serverMsg = anyErr?.response?.data?.error || anyErr?.response?.data?.message;
  if (serverMsg) return { message: String(serverMsg) };
  if (anyErr?.message) return { message: String(anyErr.message) };
  return { message: 'Request failed' };
}

async function req<T>(fn: () => Promise<{ data: T }>): Promise<Result<T>> {
  try {
    const res = await fn();
    return { data: res.data, error: null };
  } catch (err) {
    return { data: null as unknown as T, error: toError(err) };
  }
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function listNotifications(opts?: { limit?: number }): Promise<Result<any[]>> {
  const r = await req<{ notifications: any[] }>(() =>
    api.get('/api/ixverify/notifications', { params: opts?.limit ? { limit: opts.limit } : {} }),
  );
  return { data: (r.data?.notifications as any[]) || [], error: r.error };
}

export async function getUnreadNotificationCount(): Promise<Result<number>> {
  const r = await req<{ count: number }>(() => api.get('/api/ixverify/notifications/unread-count'));
  return { data: r.data?.count ?? 0, error: r.error };
}

export async function createNotifications(payload: any | { notifications: any[] }): Promise<Result<null>> {
  const body = Array.isArray(payload) ? { notifications: payload } : payload;
  const r = await req<any>(() => api.post('/api/ixverify/notifications', body));
  return { data: null, error: r.error };
}

export async function setNotificationRead(id: string, read: boolean): Promise<Result<null>> {
  const r = await req<any>(() => api.patch(`/api/ixverify/notifications/${id}`, { read }));
  return { data: null, error: r.error };
}

export async function markAllNotificationsReadApi(): Promise<Result<null>> {
  const r = await req<any>(() => api.patch('/api/ixverify/notifications/read-all', {}));
  return { data: null, error: r.error };
}

export async function deleteNotification(id: string): Promise<Result<null>> {
  const r = await req<any>(() => api.delete(`/api/ixverify/notifications/${id}`));
  return { data: null, error: r.error };
}

export async function notifyAdminsMismatchApi(input: {
  siteId: string;
  performedByEmail: string;
  verificationId: string;
  ixrsNumber: string;
  vialNumber: string;
}): Promise<Result<null>> {
  const r = await req<any>(() => api.post('/api/ixverify/notifications/notify-admins-mismatch', input));
  return { data: null, error: r.error };
}

// ---------------------------------------------------------------------------
// Saved recipients
// ---------------------------------------------------------------------------

export async function listSavedRecipients(siteId?: string): Promise<Result<any[]>> {
  const r = await req<{ recipients: any[] }>(() =>
    api.get('/api/ixverify/saved-recipients', { params: siteId ? { site_id: siteId } : {} }),
  );
  return { data: (r.data?.recipients as any[]) || [], error: r.error };
}

export async function createSavedRecipient(input: { site_id?: string; email: string; label?: string | null }): Promise<Result<any>> {
  const r = await req<{ recipient: any }>(() => api.post('/api/ixverify/saved-recipients', input));
  return { data: r.data?.recipient ?? null, error: r.error };
}

export async function updateSavedRecipient(id: string, input: { email: string; label?: string | null }): Promise<Result<null>> {
  const r = await req<any>(() => api.patch(`/api/ixverify/saved-recipients/${id}`, input));
  return { data: null, error: r.error };
}

export async function deleteSavedRecipient(id: string): Promise<Result<null>> {
  const r = await req<any>(() => api.delete(`/api/ixverify/saved-recipients/${id}`));
  return { data: null, error: r.error };
}

// ---------------------------------------------------------------------------
// Verifications
// ---------------------------------------------------------------------------

export async function listVerifications(opts?: { siteId?: string; includeUser?: boolean }): Promise<Result<any[]>> {
  const params: Record<string, string> = {};
  if (opts?.siteId) params.site_id = opts.siteId;
  if (opts?.includeUser) params.include = 'user';
  const r = await req<{ verifications: any[] }>(() => api.get('/api/ixverify/verifications', { params }));
  return { data: (r.data?.verifications as any[]) || [], error: r.error };
}

export async function listShipmentVerifications(opts?: { siteId?: string }): Promise<Result<any[]>> {
  const params: Record<string, string> = {};
  if (opts?.siteId) params.site_id = opts.siteId;
  const r = await req<{ shipments: any[] }>(() => api.get('/api/ixverify/shipment-verifications', { params }));
  return { data: (r.data?.shipments as any[]) || [], error: r.error };
}

export async function insertShipmentVerification(row: any): Promise<Result<null>> {
  const r = await req<any>(() => api.post('/api/ixverify/shipment-verifications', row));
  return { data: null, error: r.error };
}

/** Fresh presigned GET URL for a verification's report PDF (replaces client-side createSignedUrl). */
export async function getVerificationPdfUrl(id: string): Promise<Result<{ url: string }>> {
  const r = await req<{ url: string }>(() => api.get(`/api/ixverify/verifications/${id}/pdf-url`));
  return { data: r.data, error: r.error };
}

// ---------------------------------------------------------------------------
// Audit log (append-only)
// ---------------------------------------------------------------------------

export async function insertAuditLog(entry: {
  site_id?: string | null;
  action: string;
  target_type: string;
  target_id?: string | null;
  details?: Record<string, unknown> | null;
}): Promise<Result<null>> {
  const r = await req<any>(() => api.post('/api/ixverify/audit-log', entry));
  return { data: null, error: r.error };
}

// ---------------------------------------------------------------------------
// Settings / profile
// ---------------------------------------------------------------------------

export async function getSite(id: string): Promise<Result<any>> {
  const r = await req<{ site: any }>(() => api.get(`/api/ixverify/sites/${id}`));
  return { data: r.data?.site ?? null, error: r.error };
}

export async function updateProfileName(fullName: string): Promise<Result<null>> {
  const r = await req<any>(() => api.patch('/api/ixverify/profile', { full_name: fullName }));
  return { data: null, error: r.error };
}

// ---------------------------------------------------------------------------
// Admin: users / sites / invitations
// (identity-table DATA; the AUTH edge functions create-user / send-invitation /
//  accept-invitation / self-service-signup stay on Supabase Auth until Phase 8.)
// ---------------------------------------------------------------------------

export async function adminListUsers(opts?: { siteId?: string }): Promise<Result<any[]>> {
  const params: Record<string, string> = {};
  if (opts?.siteId) params.site_id = opts.siteId;
  const r = await req<{ users: any[] }>(() => api.get('/api/ixverify/admin/users', { params }));
  return { data: (r.data?.users as any[]) || [], error: r.error };
}

export async function adminUserExists(email: string): Promise<Result<{ exists: boolean; user: any | null }>> {
  const r = await req<{ exists: boolean; user: any }>(() =>
    api.get('/api/ixverify/admin/users/exists', { params: { email } }),
  );
  return { data: r.data, error: r.error };
}

export async function adminUpdateUserRole(id: string, role: string): Promise<Result<null>> {
  const r = await req<any>(() => api.patch(`/api/ixverify/admin/users/${id}/role`, { role }));
  return { data: null, error: r.error };
}

export async function adminUpdateUser(id: string, updates: Record<string, unknown>): Promise<Result<null>> {
  const r = await req<any>(() => api.patch(`/api/ixverify/admin/users/${id}`, updates));
  return { data: null, error: r.error };
}

export async function adminDeleteUser(id: string): Promise<Result<null>> {
  const r = await req<any>(() => api.delete(`/api/ixverify/admin/users/${id}`));
  return { data: null, error: r.error };
}

export async function adminListSites(opts?: { siteId?: string }): Promise<Result<any[]>> {
  const params: Record<string, string> = {};
  if (opts?.siteId) params.site_id = opts.siteId;
  const r = await req<{ sites: any[] }>(() => api.get('/api/ixverify/admin/sites', { params }));
  return { data: (r.data?.sites as any[]) || [], error: r.error };
}

export async function adminCreateSite(site: Record<string, unknown>): Promise<Result<any>> {
  const r = await req<{ site: any }>(() => api.post('/api/ixverify/admin/sites', site));
  return { data: r.data?.site ?? null, error: r.error };
}

export async function adminListInvitations(opts?: { pending?: boolean; notExpired?: boolean; siteId?: string }): Promise<Result<any[]>> {
  const params: Record<string, string> = {};
  if (opts?.pending) params.pending = '1';
  if (opts?.notExpired) params.not_expired = '1';
  if (opts?.siteId) params.site_id = opts.siteId;
  const r = await req<{ invitations: any[] }>(() => api.get('/api/ixverify/admin/invitations', { params }));
  return { data: (r.data?.invitations as any[]) || [], error: r.error };
}

export async function adminInvitationByEmail(email: string): Promise<Result<{ invitation: any | null; exists: boolean }>> {
  const r = await req<{ invitation: any; exists: boolean }>(() =>
    api.get('/api/ixverify/admin/invitations/by-email', { params: { email } }),
  );
  return { data: r.data, error: r.error };
}

export async function adminCreateInvitation(input: { email: string; site_id?: string | null; role: string }): Promise<Result<any>> {
  const r = await req<{ invitation: any }>(() => api.post('/api/ixverify/admin/invitations', input));
  return { data: r.data?.invitation ?? null, error: r.error };
}

export async function adminDeleteInvitation(id: string): Promise<Result<null>> {
  const r = await req<any>(() => api.delete(`/api/ixverify/admin/invitations/${id}`));
  return { data: null, error: r.error };
}

/** PUBLIC invitation lookup by token (signup page, pre-auth). */
export async function getInvitationByToken(token: string): Promise<Result<any>> {
  const r = await req<{ invitation: any }>(() => api.get(`/api/ixverify/invitations/token/${encodeURIComponent(token)}`));
  return { data: r.data?.invitation ?? null, error: r.error };
}

// ---------------------------------------------------------------------------
// Login lockout (PUBLIC — runs pre-auth; JWT-exempt on the server)
// ---------------------------------------------------------------------------

export async function isAccountLocked(email: string): Promise<Result<boolean>> {
  const r = await req<{ locked: boolean }>(() =>
    api.post('/api/ixverify/login/is-account-locked', { email: email.toLowerCase() }),
  );
  return { data: r.data?.locked ?? false, error: r.error };
}

export async function incrementFailedLogin(email: string): Promise<Result<any>> {
  const r = await req<{ result: any }>(() =>
    api.post('/api/ixverify/login/increment-failed', { email: email.toLowerCase() }),
  );
  return { data: r.data?.result ?? null, error: r.error };
}

export async function resetFailedLogin(email: string, method = 'password'): Promise<Result<null>> {
  const r = await req<any>(() =>
    api.post('/api/ixverify/login/reset-failed', { email: email.toLowerCase(), method }),
  );
  return { data: null, error: r.error };
}

// ---------------------------------------------------------------------------
// OCR / report (ported edge functions). Return { data, error } to match the
// former supabase.functions.invoke() call sites exactly.
// ---------------------------------------------------------------------------

export async function invokeExtractIds(body: any): Promise<Result<any>> {
  return req<any>(() => api.post('/api/ixverify/extract-ids', body));
}

export async function invokeGenerateAndSend(body: any): Promise<Result<any>> {
  return req<any>(() => api.post('/api/ixverify/generate-and-send', body));
}
